import { useState, useEffect, useMemo } from 'react';
import { SaleOffer, BuyRequest } from '../models/Marketplace';
import { User } from '../models/User';
import { 
  canUserDeleteOffer, 
  canUserDeleteRequest,
  getMarketStatistics,
  validateOffer,
  validateBuyRequest,
  formatCurrency
} from '../utils/marketplaceUtils';
import {
  getAllSaleOffers,
  getAllBuyRequests,
  addSaleOffer as dbAddSaleOffer,
  addBuyRequest as dbAddBuyRequest,
  deleteSaleOffer as dbDeleteSaleOffer,
  deleteBuyRequest as dbDeleteBuyRequest,
  applyMarketplaceFixes
} from '../data/marketplaceDatabase';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';

interface UseMarketplaceDataReturn {
  // Data
  salesOffers: SaleOffer[];
  buyRequests: BuyRequest[];
  sortedSalesOffers: SaleOffer[];
  sortedBuyRequests: BuyRequest[];
  isLoading: boolean;
  
  // Statistics
  highestBuyRequest: number;
  lowestSalesOffer: number;
  marketStats: ReturnType<typeof getMarketStatistics>;
  
  // Actions
  addSaleOffer: (offer: SaleOffer) => Promise<void>;
  addBuyRequest: (request: BuyRequest) => Promise<void>;
  deleteSaleOffer: (offerId: string) => Promise<void>;
  deleteBuyRequest: (requestId: string) => Promise<void>;

  
  // Validation
  validateSaleOffer: (offer: Partial<SaleOffer>) => string[];
  validateBuyRequestData: (request: Partial<BuyRequest>) => string[];
  
  // Permissions
  canUserDelete: (item: SaleOffer | BuyRequest, user: User) => boolean;
}

// Default data for initialization
const getDefaultSalesOffers = (): SaleOffer[] => [
  {
    id: '1',
    sharePrice: '45.75',
    numberOfShares: '500',
    timestamp: '2023-07-15T10:30:00',
    confirmationTimestamp: '2023-07-15T10:32:15',
    legalOwnershipConfirmed: true,
    accuracyConfirmed: true,
    ipAddress: '192.168.1.1',
    country: 'United States',
    isp: 'Comcast',
    seller: '1',
    showFullName: true
  },
  {
    id: '2',
    sharePrice: '46.25',
    numberOfShares: '300',
    timestamp: '2023-07-16T11:20:00',
    confirmationTimestamp: '2023-07-16T11:22:30',
    legalOwnershipConfirmed: true,
    accuracyConfirmed: true,
    ipAddress: '192.168.1.2',
    country: 'Norway',
    isp: 'Telenor',
    seller: '2',
    showFullName: true
  }
];

const getDefaultBuyRequests = (): BuyRequest[] => [
  {
    id: '1',
    sharePrice: '44.50',
    numberOfShares: '200',
    requestTimestamp: '2023-07-14T09:25:00',
    confirmationTimestamp: '2023-07-14T09:27:45',
    sufficientFundsConfirmed: true,
    accuracyConfirmed: true,
    ipAddress: '192.168.1.5',
    country: 'United States',
    isp: 'AT&T',
    requester: '3',
    showFullName: true
  },
  {
    id: '2',
    sharePrice: '45.00',
    numberOfShares: '150',
    requestTimestamp: '2023-07-17T14:10:00',
    confirmationTimestamp: '2023-07-17T14:12:20',
    sufficientFundsConfirmed: true,
    accuracyConfirmed: true,
    ipAddress: '192.168.1.6',
    country: 'United States',
    isp: 'Verizon',
    requester: '4',
    showFullName: false
  }
];

export const useMarketplaceData = (): UseMarketplaceDataReturn => {
  // Initialize state from centralized database
  const [salesOffersState, setSalesOffersState] = useState<SaleOffer[]>([]);
  const [buyRequestsState, setBuyRequestsState] = useState<BuyRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to refresh data from Firestore
  const refreshDataFromFirestore = async () => {
    try {
      // Load sale offers from Firestore
      const saleOffersQuery = query(collection(db, 'saleOffers'), orderBy('timestamp', 'desc'));
      const saleOffersSnapshot = await getDocs(saleOffersQuery);
      const saleOffers = saleOffersSnapshot.docs.map(doc => doc.data() as SaleOffer);
      setSalesOffersState(saleOffers);
      
      // Load buy requests from Firestore
      const buyRequestsQuery = query(collection(db, 'buyRequests'), orderBy('requestTimestamp', 'desc'));
      const buyRequestsSnapshot = await getDocs(buyRequestsQuery);
      const buyRequests = buyRequestsSnapshot.docs.map(doc => doc.data() as BuyRequest);
      setBuyRequestsState(buyRequests);
    } catch (error) {
      console.error('Failed to refresh data from Firestore:', error);
    }
  };

  // Load data from centralized database
  useEffect(() => {
    const loadMarketplaceData = async () => {
      try {
        setIsLoading(true);
        
        // Apply any necessary fixes first
        applyMarketplaceFixes();
        
        // Load data directly from Firestore
        await refreshDataFromFirestore();
      } catch (error) {
        console.error('Failed to load marketplace data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMarketplaceData();
  }, []);

  // Sorted data
  const sortedSalesOffers = useMemo(() => {
    return [...salesOffersState].sort((a, b) => 
      parseFloat(b.sharePrice) - parseFloat(a.sharePrice)
    );
  }, [salesOffersState]);

  const sortedBuyRequests = useMemo(() => {
    return [...buyRequestsState].sort((a, b) => 
      parseFloat(b.sharePrice) - parseFloat(a.sharePrice)
    );
  }, [buyRequestsState]);

  // Market statistics
  const highestBuyRequest = useMemo(() => {
    return buyRequestsState.length > 0 
      ? Math.max(...buyRequestsState.map(req => parseFloat(req.sharePrice)))
      : 0;
  }, [buyRequestsState]);

  const lowestSalesOffer = useMemo(() => {
    return salesOffersState.length > 0 
      ? Math.min(...salesOffersState.map(offer => parseFloat(offer.sharePrice)))
      : Infinity;
  }, [salesOffersState]);

  const marketStats = useMemo(() => {
    return getMarketStatistics(salesOffersState, buyRequestsState);
  }, [salesOffersState, buyRequestsState]);

  // Actions
  const addSaleOffer = async (offer: SaleOffer) => {
    try {
      await dbAddSaleOffer(offer);
      
      // Refresh data directly from Firestore
      await refreshDataFromFirestore();
    } catch (error) {
      console.error('Failed to add sale offer:', error);
      throw error;
    }
  };

  const addBuyRequest = async (request: BuyRequest) => {
    try {
      await dbAddBuyRequest(request);
      
      // Refresh data directly from Firestore
      await refreshDataFromFirestore();
    } catch (error) {
      console.error('Failed to add buy request:', error);
      throw error;
    }
  };

  const deleteSaleOffer = async (offerId: string) => {
    try {
      await dbDeleteSaleOffer(offerId);
      // Refresh data directly from Firestore
      await refreshDataFromFirestore();
    } catch (error) {
      console.error('Failed to delete sale offer:', error);
      throw error;
    }
  };

  const deleteBuyRequest = async (requestId: string) => {
    try {
      await dbDeleteBuyRequest(requestId);
      // Refresh data directly from Firestore
      await refreshDataFromFirestore();
    } catch (error) {
      console.error('Failed to delete buy request:', error);
      throw error;
    }
  };



  // Validation functions
  const validateSaleOffer = (offer: Partial<SaleOffer>) => {
    return validateOffer(offer);
  };

  const validateBuyRequestData = (request: Partial<BuyRequest>) => {
    return validateBuyRequest(request);
  };

  // Permission checks
  const canUserDelete = (item: SaleOffer | BuyRequest, user: User) => {
    if ('seller' in item) {
      return canUserDeleteOffer(item, user);
    } else {
      return canUserDeleteRequest(item, user);
    }
  };



  return {
    // Data
    salesOffers: salesOffersState,
    buyRequests: buyRequestsState,
    sortedSalesOffers,
    sortedBuyRequests,
    isLoading,
    
    // Statistics
    highestBuyRequest,
    lowestSalesOffer,
    marketStats,
    
    // Actions
    addSaleOffer,
    addBuyRequest,
    deleteSaleOffer,
    deleteBuyRequest,

    
    // Validation
    validateSaleOffer,
    validateBuyRequestData,
    
    // Permissions
    canUserDelete
  };
}; 